import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** One selectable variant option shown under an item (name/value pair). */
export const PosItemDetailsVariantSchema = z.object({
  name: z.string(),
  value: z.string(),
});

/** A badge shown on an item row (return reason / compensation type). POS does
 * not render the badge color, so only the text crosses the wire. */
export const PosItemDetailsBadgeSchema = z.object({ text: z.string() });

/**
 * One display-only item-details row. Ordered to match the client's
 * `getCombinedReturnItemsGrouped` so interactive item N pairs with view item N.
 */
export const PosItemDetailsItemSchema = z.object({
  id: z.string(),
  pendingReturnItemId: z.string(),
  imageSrc: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  quantity: z.number(),
  variants: z.array(PosItemDetailsVariantSchema),
  formattedPrice: z.string(),
  currency: createEnum(Currency),
  price: z.string(),
  itemValue: z.string().nullable(),
  /** Primary "Ready for return" badge, or null when no pending item / repair. */
  badgeText: z.string().nullable(),
  returnTypeBadges: z.array(PosItemDetailsBadgeSchema),
  presentmentCurrency: z.string(),
  exchangeRate: z.number(),
  variantId: z.string().nullable(),
  hideQuantity: z.boolean(),
  collections: z.array(z.string()),
});

/**
 * Display-ready item-details screen state, computed server-side (replaces the
 * client `useItemDetails` display fork; interactive actions stay client).
 */
export const PosItemDetailsViewSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  /** Whether every selected item has a pending item, so Continue is allowed. */
  allowAdvancing: z.boolean(),
  /** Merchant-created return with nothing to add yet: offer the Skip button. */
  showSkipButton: z.boolean(),
  items: z.array(PosItemDetailsItemSchema),
});

export type PosItemDetailsView = z.infer<typeof PosItemDetailsViewSchema>;
export type PosItemDetailsItem = z.infer<typeof PosItemDetailsItemSchema>;
export type PosItemDetailsBadge = z.infer<typeof PosItemDetailsBadgeSchema>;

export const getPosItemDetailsView = {
  name: "getPosItemDetailsView" as const,
  input: z.object({ draftReturnId: z.string() }),
  output: z.object({ view: PosItemDetailsViewSchema }),
};
